import { createContext, useContext, useState, useCallback, useRef } from 'react';
import { Api } from 'Utils/api';
import { reportConfigs } from 'Utils/reportConfigs';

interface ReportState {
  data: any[];
  loading: boolean;
  error: string | null;
  totalPages: number;
  currentPage: number;
}

const initialState: ReportState = {
  data: [],
  loading: false,
  error: null,
  totalPages: 1,
  currentPage: 0,
};

export const CustomReportsContext = createContext({});

export const CustomReportsFunctions = () => {
  const [projectsByStatus, setProjectsByStatus] = useState<ReportState>(initialState);
  const [recentTimecards, setRecentTimecards] = useState<ReportState>(initialState);
  const [equipmentDeployments, setEquipmentDeployments] = useState<ReportState>(initialState);
  const [projectsOverTime, setProjectsOverTime] = useState<ReportState>(initialState);
  const [projectList, setProjectList] = useState<ReportState>(initialState);
  const [projectAudits, setProjectAudits] = useState<ReportState>(initialState);

  const fetchReport = useCallback(
    async (
      configKey: string,
      setter: (state: ReportState) => void,
      pageOverride?: number,
      perPageOverride?: number,
      filtersOverride?: any[]
    ) => {
      const config = reportConfigs[configKey];
      if (!config) return;

      setter({ data: [], loading: true, error: null, totalPages: 1, currentPage: pageOverride || 0 });

      try {
        const response = await Api.post('/custom-reports/preview', {
          root_entity: config.root_entity,
          columns: config.columns,
          filters: filtersOverride || config.filters,
          sorts: config.sorts,
          per_page: perPageOverride || config.per_page,
          page: (pageOverride || 0) + 1,
        });

        const { data } = response;
        setter({
          data: data?.data || [],
          loading: false,
          error: null,
          totalPages: data?.last_page || 1,
          currentPage: pageOverride || 0,
        });
      } catch (err: any) {
        setter({
          data: [],
          loading: false,
          error: err?.response?.data?.message || err?.message || 'Failed to load report',
          totalPages: 1,
          currentPage: 0,
        });
      }
    },
    []
  );

  const fetchProjectsByStatus = useCallback(() => {
    fetchReport('projectsByStatus', setProjectsByStatus);
  }, [fetchReport]);

  const fetchRecentTimecards = useCallback(
    (page = 0) => {
      fetchReport('recentTimecards', setRecentTimecards, page, 25);
    },
    [fetchReport]
  );

  const fetchEquipmentDeployments = useCallback(() => {
    fetchReport('equipmentDeployments', setEquipmentDeployments);
  }, [fetchReport]);

  const fetchProjectsOverTime = useCallback(() => {
    fetchReport('projectsOverTime', setProjectsOverTime);
  }, [fetchReport]);

  const projectListRequestId = useRef(0);

  const fetchProjectList = useCallback(
    async (search = '') => {
      const requestId = ++projectListRequestId.current;
      const filters = search
        ? [
            { field: 'project.uid', operator: 'like', value: search },
          ]
        : [];

      setProjectList((prev) => ({ ...prev, loading: true, error: null }));

      try {
        const config = reportConfigs.projectList;
        const response = await Api.post('/custom-reports/preview', {
          root_entity: config.root_entity,
          columns: config.columns,
          filters,
          sorts: config.sorts,
          per_page: config.per_page,
          page: 1,
        });

        if (requestId !== projectListRequestId.current) return;

        const { data } = response;
        setProjectList({
          data: data?.data || [],
          loading: false,
          error: null,
          totalPages: data?.last_page || 1,
          currentPage: 0,
        });
      } catch (err: any) {
        if (requestId !== projectListRequestId.current) return;

        setProjectList({
          data: [],
          loading: false,
          error: err?.response?.data?.message || err?.message || 'Failed to load projects',
          totalPages: 1,
          currentPage: 0,
        });
      }
    },
    []
  );

  const fetchProjectAudits = useCallback(
    async (projectUid: string) => {
      setProjectAudits({ data: [], loading: true, error: null, totalPages: 1, currentPage: 0 });

      try {
        const response = await Api.get(`/projects/${projectUid}/audit-log`);
        const { data } = response;
        setProjectAudits({
          data: data?.data || [],
          loading: false,
          error: null,
          totalPages: 1,
          currentPage: 0,
        });
      } catch (err: any) {
        setProjectAudits({
          data: [],
          loading: false,
          error: err?.response?.data?.message || err?.message || 'Failed to load audit log',
          totalPages: 1,
          currentPage: 0,
        });
      }
    },
    []
  );

  return {
    projectsByStatus,
    recentTimecards,
    equipmentDeployments,
    projectsOverTime,
    projectList,
    projectAudits,
    fetchProjectsByStatus,
    fetchRecentTimecards,
    fetchEquipmentDeployments,
    fetchProjectsOverTime,
    fetchProjectList,
    fetchProjectAudits,
  };
};

export const useCustomReports = () => useContext(CustomReportsContext);
